import { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionCreators } from 'redux-undo'
import { unwrapResult } from '@reduxjs/toolkit'
import { useHoneyBadger } from 'apps/builder/lib/hooks/honeybadger'
import { Modal } from 'apps/studio/components/modals'
import {
  sendNotificationSelector,
  usersSelector,
  groupsSelector,
  assignedGroupsSelector,
  assignedUsersSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import {
  appNameSelector,
  appIdSelector,
  appVersionSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import {
  appSettingsActions,
  publishApp as publishCurrentForm,
} from 'apps/formsCreationKit/store/reducers/settings/appSettings'
const {
  saveAndPublishLoaded,
  toggleModal,
  updateNotification,
  assignAllUsers,
  assignAllGroups,
  unassignAllGroups,
  unassignAllUsers,
  assignGroup,
  assignUser,
  unassignGroup,
  unassignUser,
  updateFlashMessage,
  sortUsers,
  sortGroups,
} = appSettingsActions
import { updateOnPublish } from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  SortIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CloseIcon,
} from '@gocanvas/gcds/icons'

const sortMap = {
  unsorted: {
    IconComponent: SortIcon,
    nextSort: 'asc',
  },
  asc: {
    IconComponent: ArrowUpIcon,
    nextSort: 'desc',
  },
  desc: {
    IconComponent: ArrowDownIcon,
    nextSort: 'unsorted',
  },
}

const AssignmentTable = ({
  type,
  columns,
  allOptions = [],
  assignedOptions = [],
  assignAll,
  unassignAll,
  assignOne,
  unassignOne,
  sortOptions,
}) => {
  const selectAllCheckboxRef = useRef()
  const dispatch = useDispatch()
  const [sort, setSort] = useState({})
  const allOptionsSelected = assignedOptions.length === allOptions.length
  const someOptionsSelected = assignedOptions.length && !allOptionsSelected

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someOptionsSelected
    }
  }, [someOptionsSelected])

  return (
    <div className="gcds--data-table">
      <table
        className="data-table data-table--grid has-checkbox-column"
        data-column-count={`${columns.length + 1}`}
        data-title="Assignment Table"
      >
        <thead className="table--header">
          <tr className="table--row table--header-row">
            <th className="table--cell table--header-cell">
              <label className="gcds--checkbox input-fields__label">
                <input
                  ref={selectAllCheckboxRef}
                  className={`input-fields__checkbox${
                    someOptionsSelected ? ' checkbox--indeterminate' : ''
                  }`}
                  id="checkbox-assign-all"
                  name="checkbox-assign-all"
                  type="checkbox"
                  checked={allOptionsSelected}
                  onChange={() =>
                    dispatch(allOptionsSelected ? unassignAll() : assignAll())
                  }
                />
              </label>
            </th>
            {columns.map((columnName, columnIndex) => {
              const { IconComponent, nextSort } =
                sort.column === columnName
                  ? sortMap[sort.order]
                  : sortMap.unsorted
              return (
                <th
                  key={`${columnName}-${columnIndex}`}
                  className="table--cell table--header-cell data-table--sortable"
                  onClick={() => {
                    setSort({
                      column: columnName,
                      order: nextSort,
                    })
                    if (nextSort != 'unsorted') {
                      dispatch(
                        sortOptions({
                          sortOrder: nextSort,
                          sortKey: columnName.toLowerCase(),
                        })
                      )
                    }
                  }}
                >
                  {columnName}
                  <button className="gcds--button button--icon" type="button">
                    <IconComponent />
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className="table--body">
          {allOptions.map((row, ri) => {
            const rowChecked = assignedOptions.includes(row.id)
            return (
              <tr className="table--row" key={`${ri}_row`}>
                <td className="table--cell">
                  <label className="gcds--checkbox input-fields__label">
                    <input
                      className="input-fields__checkbox"
                      id={`checkbox-select-${type}-${ri}`}
                      name={`checkbox-select-${type}-${ri}`}
                      type="checkbox"
                      checked={rowChecked}
                      onChange={() =>
                        dispatch(
                          rowChecked ? unassignOne(row.id) : assignOne(row.id)
                        )
                      }
                    />
                  </label>
                </td>
                {columns.map(columnName => (
                  <td
                    className="table--cell"
                    data-title={columnName}
                    key={`${ri}_${columnName}`}
                  >
                    {row[columnName.toLowerCase()]}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const AssignmentTabs = ({ activeTab, setActiveTab, tabConfig, children }) => (
  <div className="gcds--tabs">
    <div className="tabs--group" role="tablist" aria-label="GCDS Tabs">
      {Object.entries(tabConfig).map(([key, value], index) => {
        const isActive = activeTab === key
        const tabName = key.charAt(0).toUpperCase() + key.slice(1)
        const { assignedOptions = [], allOptions = [] } = value
        return (
          <button
            key={`tab-${key}-${index}`}
            id={`tab-${key}`}
            className={`tab${isActive ? ' tab--active' : ''}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => setActiveTab(key)}
          >
            {`${tabName} (${assignedOptions.length} of ${allOptions.length} selected)`}
          </button>
        )
      })}
    </div>
    <div
      className="tabs--content"
      role="tabpanel"
      id={`tabpanel-${activeTab}`}
      aria-labelledby={`tab-${activeTab}`}
    >
      {children}
    </div>
  </div>
)

export const PublishModal = () => {
  const dispatch = useDispatch()
  const [notify] = useHoneyBadger()
  const [activeTab, setActiveTab] = useState('users')
  const formVersion = useSelector(appVersionSelector)
  const formId = useSelector(appIdSelector)
  const formName = useSelector(appNameSelector)
  const sendNotification = useSelector(sendNotificationSelector)
  const allUsers = useSelector(usersSelector)
  const allGroups = useSelector(groupsSelector)
  const assignedUsers = useSelector(assignedUsersSelector)
  const assignedGroups = useSelector(assignedGroupsSelector)
  const modalRef = useRef()

  const tabConfig = {
    users: {
      columns: ['Name', 'Email'],
      allOptions: allUsers,
      assignedOptions: assignedUsers,
      unassignAll: unassignAllUsers,
      assignAll: assignAllUsers,
      unassignOne: unassignUser,
      assignOne: assignUser,
      sortOptions: sortUsers,
    },
    groups: {
      columns: ['Name', 'Description'],
      allOptions: allGroups,
      assignedOptions: assignedGroups,
      unassignAll: unassignAllGroups,
      assignAll: assignAllGroups,
      unassignOne: unassignGroup,
      assignOne: assignGroup,
      sortOptions: sortGroups,
    },
  }

  const closeDialog = () => {
    dispatch(saveAndPublishLoaded(false))
    dispatch(
      toggleModal({
        activeModal: '',
      })
    )
  }

  const publishForm = async () => {
    try {
      unwrapResult(
        await dispatch(
          publishCurrentForm({
            appId: formId,
            assignedUsers,
            assignedGroups,
            send_notification: sendNotification,
          })
        )
      )
      dispatch(
        updateFlashMessage({
          message: `Published ${formName} version ${formVersion}`,
          type: 'success',
        })
      )
      dispatch(ActionCreators.clearHistory())
      dispatch(updateOnPublish())
    } catch (e) {
      notify(e)
      dispatch(
        updateFlashMessage({
          message: 'Something went wrong',
          type: 'error',
        })
      )
    } finally {
      closeDialog()
    }
  }

  return (
    <Modal className="gcds--modal publish--modal" ref={modalRef}>
      <div className="modal--header">
        <div className="modal--title">Publish</div>
        <div className="modal--controls">
          <button
            className="gcds--button button--icon modal--close-icon"
            type="button"
            onClick={closeDialog}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className="modal--content">
        <AssignmentTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabConfig={tabConfig}
        >
          <AssignmentTable type={activeTab} {...tabConfig[activeTab]} />
        </AssignmentTabs>
      </div>
      <div className="modal--footer modal--footer--split">
        <div className="gcds--toggle">
          <label className="toggle--label" htmlFor="send-notification--toggle">
            <div className="label--text">Send Notification</div>
          </label>
          <div className="toggle--element">
            <input
              className="toggle--input"
              id="send-notification--toggle"
              type="checkbox"
              checked={sendNotification}
              onChange={() => dispatch(updateNotification(!sendNotification))}
            />
          </div>
        </div>
        <button
          className="gcds--button button--primary"
          type="button"
          onClick={() => publishForm()}
        >
          Publish to device
        </button>
      </div>
    </Modal>
  )
}
